import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback } from 'react';
import { keepPreviousDataWhen } from '@/hooks/use-query-loading-state';
import { invalidateWebAddressStatus } from '@/features/public-names/invalidate-web-address-status';
import { invalidateMarketingBackedReports } from '@/features/reports/ui/invalidate-marketing-backed-reports';
import {
  getGetMarketingAnalyticsBioQueryKey,
  getGetMarketingAnalyticsCampaignsIdReportQueryKey,
  getGetMarketingAnalyticsCampaignsQueryKey,
  getGetMarketingBulkEmailIdBookingsQueryKey,
  getGetMarketingBulkEmailIdRecipientsQueryKey,
  getGetMarketingBulkSmsIdQueryKey,
  getGetMarketingBulkSmsIdPlanQuoteQueryKey,
  getGetMarketingBulkSmsQueryKey,
  getGetMarketingCampaignsIdAdSpendQueryKey,
  getGetMarketingCampaignCopyTargetsQueryKey,
  getGetMarketingCampaignsIdAdTrackingQueryKey,
  getGetMarketingCampaignsIdMetaAdsQueryKey,
  getGetMarketingSettingsAdTrackingMetaAdsQueryKey,
  getGetMarketingCampaignsIdAutomationActivityQueryKey,
  getGetMarketingCampaignsIdAutomationQueryKey,
  getGetMarketingCampaignsIdGiveawayLiveDrawQueryKey,
  getGetMarketingCampaignsIdGiveawayQueryKey,
  getGetMarketingCampaignsIdGiveawaySummaryQueryKey,
  getGetMarketingCampaignsIdGiveawayThankYouGiftsPreviewQueryKey,
  getGetMarketingCampaignsIdQueryKey,
  getGetMarketingCampaignsIdVoucherQueryKey,
  getGetMarketingCampaignsIdVoucherPrintKitQueryKey,
  getGetMarketingCampaignsIdVoucherResultsQueryKey,
  getGetMarketingCampaignsIdVoucherTextClientsQueryKey,
  getGetMarketingCampaignsQueryKey,
  getGetMarketingLeadsIdQueryKey,
  getGetMarketingLeadsQueryKey,
  getGetMarketingLeadsSummaryQueryKey,
  getGetMarketingLeadsFollowUpCountQueryKey,
  getGetMarketingNewClientOfferQueryKey,
  getGetMarketingQrCodesForQueryKey,
  getGetMarketingQrCodesQueryKey,
  getGetMarketingTextKeywordsQueryKey,
  getGetMarketingWorkspaceQueryKey,
} from '@/api/generated/marketing/marketing';
import type {
  GetMarketingCampaignsId200,
  GetMarketingBulkEmailIdRecipientsGroup,
  GetMarketingAnalyticsBioParams,
  GetMarketingAnalyticsCampaignsIdReportParams,
  GetMarketingAnalyticsCampaignsParams,
  GetMarketingCampaignsParams,
  GetMarketingLeadsParams,
  GetMarketingLeadsSummaryParams,
  GetMarketingQrCodesForParams,
} from '@/api/generated/schemas';

/** Query keys are scoped by the routed salon so agency users never see another salon's cache. */
export const marketingQueryKeys = {
  workspace: (orgId: string) => [...getGetMarketingWorkspaceQueryKey(), orgId] as const,
  campaigns: (orgId: string, params?: GetMarketingCampaignsParams) =>
    [...getGetMarketingCampaignsQueryKey(params), orgId] as const,
  campaign: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdQueryKey(campaignId), orgId] as const,
  /** A voucher campaign's setup (`GET /marketing/campaigns/{id}/voucher`). */
  voucher: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdVoucherQueryKey(campaignId), orgId] as const,
  /** A voucher's printed QR + printed funnel (`GET /marketing/campaigns/{id}/voucher/print-kit`). */
  voucherPrintKit: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdVoucherPrintKitQueryKey(campaignId), orgId] as const,
  /** A voucher's "Text it to your clients" groups, counts and past sends (`GET …/voucher/text-clients`). */
  voucherTextClients: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdVoucherTextClientsQueryKey(campaignId), orgId] as const,
  /** A voucher's results on the hub, over its whole run (`GET …/voucher/results`, no window). */
  voucherResults: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdVoucherResultsQueryKey(campaignId), orgId] as const,
  /** A giveaway's setup (`GET …/giveaway`). */
  giveaway: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdGiveawayQueryKey(campaignId), orgId] as const,
  /** A giveaway's hub numbers (`GET …/giveaway/summary`). */
  giveawaySummary: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdGiveawaySummaryQueryKey(campaignId), orgId] as const,
  /** The filmed draw screen of one prize (`GET …/giveaway/live-draw`). */
  giveawayLiveDraw: (orgId: string, campaignId: string, prizeId: string) =>
    [...getGetMarketingCampaignsIdGiveawayLiveDrawQueryKey(campaignId, { prizeId }), orgId] as const,
  /** The thank-you gift groups and message (`GET …/giveaway/thank-you-gifts/preview`). */
  giveawayThankYou: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdGiveawayThankYouGiftsPreviewQueryKey(campaignId), orgId] as const,
  automation: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdAutomationQueryKey(campaignId), orgId] as const,
  automationActivity: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdAutomationActivityQueryKey(campaignId), orgId] as const,
  campaignAdTracking: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdAdTrackingQueryKey(campaignId), orgId] as const,
  campaignMetaAds: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdMetaAdsQueryKey(campaignId), orgId] as const,
  metaAdsSettings: (orgId: string) => [...getGetMarketingSettingsAdTrackingMetaAdsQueryKey(), orgId] as const,
  /** Other salons this viewer may copy campaigns into — per viewer + routed salon, not per campaign. */
  campaignCopyTargets: (orgId: string) => [...getGetMarketingCampaignCopyTargetsQueryKey(), orgId] as const,
  campaignAdSpend: (orgId: string, campaignId: string) =>
    [...getGetMarketingCampaignsIdAdSpendQueryKey(campaignId), orgId] as const,
  leads: (orgId: string, params?: GetMarketingLeadsParams) =>
    [...getGetMarketingLeadsQueryKey(params), orgId] as const,
  leadsSummary: (orgId: string, params?: GetMarketingLeadsSummaryParams) =>
    [...getGetMarketingLeadsSummaryQueryKey(params), orgId] as const,
  /** The Leads nav badge's count alone (`GET /marketing/leads/follow-up-count`). */
  leadsFollowUpCount: (orgId: string) => [...getGetMarketingLeadsFollowUpCountQueryKey(), orgId] as const,
  lead: (orgId: string, submissionId: string) =>
    [...getGetMarketingLeadsIdQueryKey(submissionId), orgId] as const,
  bulkSends: (orgId: string) => [...getGetMarketingBulkSmsQueryKey(), orgId] as const,
  /** Texts & Emails page (paged, per channel filter): a child of `bulkSends`, so its invalidations refresh it too. */
  bulkSendsHistory: (orgId: string, channel: 'SMS' | 'EMAIL' | undefined) =>
    [...getGetMarketingBulkSmsQueryKey(), orgId, 'history', channel ?? 'ALL'] as const,
  bulkSend: (orgId: string, id: string) => [...getGetMarketingBulkSmsIdQueryKey(id), orgId] as const,
  bulkPlanQuote: (orgId: string, id: string) => [...getGetMarketingBulkSmsIdPlanQuoteQueryKey(id), orgId] as const,
  bulkEmailRecipients: (orgId: string, id: string, group: GetMarketingBulkEmailIdRecipientsGroup) =>
    [...getGetMarketingBulkEmailIdRecipientsQueryKey(id, { group }), orgId] as const,
  /** Who booked from a bulk email (the Results card's first page; `all` = the paged "See all" list). */
  bulkEmailBookings: (orgId: string, id: string, scope: 'first' | 'all') =>
    [...getGetMarketingBulkEmailIdBookingsQueryKey(id), orgId, scope] as const,
  analyticsCampaigns: (orgId: string, params?: GetMarketingAnalyticsCampaignsParams) =>
    [...getGetMarketingAnalyticsCampaignsQueryKey(params), orgId] as const,
  campaignReport: (orgId: string, campaignId: string, params?: GetMarketingAnalyticsCampaignsIdReportParams) =>
    [...getGetMarketingAnalyticsCampaignsIdReportQueryKey(campaignId, params), orgId] as const,
  analyticsBio: (orgId: string, params?: GetMarketingAnalyticsBioParams) =>
    [...getGetMarketingAnalyticsBioQueryKey(params), orgId] as const,
  /** Marketing › QR Codes: every flyer QR of the salon (always ready, live campaigns, handed-out ended ones). */
  qrCodes: (orgId: string) => [...getGetMarketingQrCodesQueryKey(), orgId] as const,
  /** One destination's permanent code (shared QR dialog behind every old QR button). */
  qrCode: (orgId: string, params: GetMarketingQrCodesForParams) =>
    [...getGetMarketingQrCodesForQueryKey(params), orgId] as const,
};

/**
 * `placeholderData` for refining a Marketing list (search / filter / range): keeps the previous rows
 * only when they belong to this salon — every `marketingQueryKeys` key ends with `orgId`, so an
 * agency user switching salons gets a skeleton, never the other salon's rows. `isSameEntity`
 * narrows further (e.g. the funnel only keeps numbers of the SAME campaign across a range change).
 */
export function keepMarketingPreviousData(orgId: string, isSameEntity?: (previousKey: QueryKey) => boolean) {
  return keepPreviousDataWhen(
    (previousKey) => previousKey.at(-1) === orgId && (isSameEntity?.(previousKey) ?? true)
  );
}

/**
 * What a campaign write changed. Each kind refreshes only the reads that show it (a write elsewhere — offer, web
 * address, QR target — refreshes its own screens):
 * - `created`: a new draft (create, duplicate) — the campaigns list (drafts aren't measured or counted anywhere else).
 * - `draft`: the page draft was saved (editor Done, the AI's occasion guess) — the list, the campaign, and the
 *   automation editor's context (form email, booking link come from the page).
 * - `basics`: name / link / dates / note — `draft` plus the Analytics table, the campaign's report (header, live
 *   days) and a voucher's results, the Reports pages built on campaigns and the QR cards (name, dates, where the code
 *   lands).
 * - `status`: pause / resume / unpublish / archive — `basics` plus New Client Offer (a published campaign counts as
 *   one of the offer's placements), Text Keywords (where a keyword sends people) and the automation activity.
 * - `published`: a publish — `status` plus the web address status (a first publish makes it public).
 */
export type MarketingChange = 'created' | 'draft' | 'basics' | 'status' | 'published';

type MarketingRead =
  | 'list'
  | 'campaign'
  | 'automation'
  | 'automationActivity'
  | 'analytics'
  | 'report'
  | 'reports'
  | 'qr'
  | 'offer'
  | 'textKeywords'
  | 'webAddress';

const DRAFT_READS: readonly MarketingRead[] = ['list', 'campaign', 'automation'];
const BASICS_READS: readonly MarketingRead[] = [...DRAFT_READS, 'analytics', 'report', 'reports', 'qr'];
const STATUS_READS: readonly MarketingRead[] = [...BASICS_READS, 'offer', 'textKeywords', 'automationActivity'];

const READS_BY_CHANGE: Record<MarketingChange, readonly MarketingRead[]> = {
  created: ['list'],
  draft: DRAFT_READS,
  basics: BASICS_READS,
  status: STATUS_READS,
  published: [...STATUS_READS, 'webAddress'],
};

const startsWith = (prefix: string) => (query: { queryKey: QueryKey }) =>
  typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(prefix);

/**
 * Refresh the reads a campaign change shows up in (see `MarketingChange`). Mounted reads refetch, the rest go stale.
 * `saved`: the campaign as the write answered it — put straight on the hub instead of read again.
 */
export function useInvalidateMarketing(orgId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    async (change: MarketingChange, campaignId?: string, saved?: GetMarketingCampaignsId200) => {
      if (campaignId && saved) queryClient.setQueryData(marketingQueryKeys.campaign(orgId, campaignId), saved);
      const refresh = (queryKey: QueryKey) => queryClient.invalidateQueries({ queryKey });
      // Orval keys start with the path, so a bare generated key matches every params / salon variant.
      const reads: Record<MarketingRead, () => Promise<unknown>> = {
        list: () => refresh(getGetMarketingCampaignsQueryKey()),
        campaign: () => (campaignId && !saved ? refresh(marketingQueryKeys.campaign(orgId, campaignId)) : Promise.resolve()),
        automation: () => (campaignId ? refresh(marketingQueryKeys.automation(orgId, campaignId)) : Promise.resolve()),
        automationActivity: () =>
          campaignId ? refresh(marketingQueryKeys.automationActivity(orgId, campaignId)) : Promise.resolve(),
        analytics: () => refresh(getGetMarketingAnalyticsCampaignsQueryKey()),
        // The campaign's report and, for a voucher, its results (dates, min spend, limit move both).
        report: () =>
          campaignId
            ? Promise.all([
                refresh(getGetMarketingAnalyticsCampaignsIdReportQueryKey(campaignId)),
                refresh(getGetMarketingCampaignsIdVoucherResultsQueryKey(campaignId)),
              ])
            : Promise.resolve(),
        reports: () => invalidateMarketingBackedReports(queryClient),
        qr: () => Promise.all([refresh(marketingQueryKeys.qrCodes(orgId)), refresh(getGetMarketingQrCodesForQueryKey())]),
        offer: () => refresh(getGetMarketingNewClientOfferQueryKey()),
        // The list and every keyword's page (`/marketing/text-keywords/{id}`).
        textKeywords: () => queryClient.invalidateQueries({ predicate: startsWith(getGetMarketingTextKeywordsQueryKey()[0]) }),
        webAddress: () => invalidateWebAddressStatus(queryClient),
      };
      await Promise.all(READS_BY_CHANGE[change].map((read) => reads[read]()));
    },
    [orgId, queryClient]
  );
}

/** Pull the API's human message out of an Axios error for toasts. */
export { getApiErrorMessage, getApiErrorReason } from '@/lib/error-utils';


/** The API's `error.details` bag (reason, counts, …) — used to read the queued-send count off a 409. */
export function getApiErrorDetails(error: unknown): Record<string, unknown> {
  const data = (error as { response?: { data?: { error?: { details?: Record<string, unknown> } } } })?.response?.data;
  return data?.error?.details ?? {};
}

export function getApiErrorIssues(error: unknown): Array<{ path: string; message: string }> {
  const data = (error as {
    response?: { data?: { error?: { details?: { issues?: Array<{ path: string; message: string }> } } } };
  })?.response?.data;
  return data?.error?.details?.issues ?? [];
}
